import { SerializedError } from '@reduxjs/toolkit';
import React, { memo, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { PdfHighlighterProvider } from '~/components/Shared/EcPdfViewerHighlighter/components/PdfHighlighterContext';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { BasePlaceholderContentProps } from '~/eds/src/types/BasePlaceholderContentProps';
import { FileMimeType } from '~/enums/FileMimeType';
import { api } from '~/redux/api/createApi';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';
import { Uuid } from '~/types/Uuid';

import { PdfDocumentViewer } from './PdfDocumentViewer/PdfDocumentViewer';
import { ResolveDocumentCallback } from './types/document';
import { BaseDocumentViewerProps } from './types/viewer';
import { UnauthorizedDocumentViewer } from './UnauthorizedDocumentViewer';
import { UnsupportedDocumentViewer } from './UnknownDocumentViewer';
import { WordDocumentViewer } from './WordDocumentViewer/WordDocumentViewer';

type TicketDocumentParams = {
  ticketId?: Uuid;
  /** Ticket document id. Used to get the original document when downloading the OCR errors (e.g. OCR not generated yet) */
  ticketDocumentId?: Uuid;
  /** ticket version id. Used to get OCR */
  ticketVersionId?: Uuid;
};

export type Props = Omit<BaseDocumentViewerProps, 'file'> & {
  /** Renders the document given its ID */
  documentId?: Nullable<PilotId>;
  /** Renders the document given its version ID (will resolve to a `documentId` internally) */
  documentVersionId?: Nullable<PilotId>;
  /** When specified, will use the provided file and skip resolving the document file */
  file?: File;
  /** Configurable options and behaviors */
  options?: {
    /* When true and provided a documentVersionId, will automatically fetch the latest version of the document (handler) */
    autoLatestVersion?: boolean;
  };
  /** When specifying documentId or documentVersionId as `null`, be sure to attach an appropriate `placeholderContent` */
  placeholderContent?: BasePlaceholderContentProps;
  /** Callback to capture the resolved document */
  onResolveDocument?: ResolveDocumentCallback;
} & TicketDocumentParams;

export const DocumentViewer = memo((props: Props) => {
  const {
    documentId,
    documentVersionId,
    options,
    file: overrideFile,
    placeholderContent: providedPlaceholderContent,
    ticketDocumentId,
    ticketId,
    ticketVersionId,
    onResolveDocument,
  } = props;

  const intl = useIntl();

  const {
    data: resolvedDocument,
    isFetching: isLoading,
    error,
  } = api.endpoints.resolveDocument.useQuery(
    {
      documentId,
      documentVersionId,
      ticketDocumentId,
      ticketId,
      ticketVersionId,

      options,
    },
    {
      skip: Boolean(overrideFile),
    },
  );

  useEffect(() => {
    if (onResolveDocument && resolvedDocument) {
      onResolveDocument(resolvedDocument);
    }
  }, [resolvedDocument]);

  const file = resolvedDocument?.file ?? overrideFile;
  const fileType = file?.type;
  const loadingMessage = intl.formatMessage({
    id: 'DocumentViewer.loadingMessage',
    defaultMessage: 'Loading document…',
    description: 'Loading message while document is being fetched',
  });
  const isUnavailable =
    (error as SerializedError)?.message === 'Document not found.';
  const Viewer = isUnavailable
    ? UnauthorizedDocumentViewer
    : fileType && fileType in viewers
      ? viewers[fileType]
      : UnsupportedDocumentViewer;

  const loadingContent = {
    isLoading,
    message: loadingMessage,
  };

  const placeholderContent = error ? providedPlaceholderContent : undefined;

  return (
    <ContentContainer
      loadingContent={loadingContent}
      placeholderContent={placeholderContent}
    >
      <PdfHighlighterProvider>
        {file ? (
          <Viewer {...props} file={file} />
        ) : isUnavailable ? (
          <UnauthorizedDocumentViewer />
        ) : null}
      </PdfHighlighterProvider>
    </ContentContainer>
  );
});

const viewers = {
  [FileMimeType.Pdf as string]: PdfDocumentViewer,
  [FileMimeType.Html as string]: WordDocumentViewer, // Once using DocumentEditor (EJ2) as the viewer, update the map to reference Word mimetypes
};
